/**
 * Lemon Squeezy hosted-checkout URL construction.
 *
 * We redirect to Lemon Squeezy's hosted checkout rather than using the
 * `lemon.js` overlay: the overlay is a third-party script that sets its own
 * cookies, which would have to be reconciled with our opt-in consent
 * categories, and it behaves badly inside the iframes the pattern composer
 * uses. Both approaches take the same URL and the same custom-data parameter,
 * so switching later is a small change.
 *
 * The Clerk user id rides along as custom data and comes back on the webhook at
 * `meta.custom_data.user_id`, which is how the backend knows whose entitlement
 * to grant. Without it the webhook acks and writes nothing — so this builder
 * refuses to produce a URL at all when the id is missing.
 *
 * The deploy environment rides along too, at `custom_data.env`. Every test-mode
 * webhook receives every test-mode event in the store, so dev and staging each
 * get the other's checkouts; the backend drops any not tagged with its own.
 */

import { resolveDeployTier, type DeployTier } from '@/config/app';

/** Lemon Squeezy's custom-data parameter; the backend reads `custom_data.user_id`. */
const USER_ID_PARAM = 'checkout[custom][user_id]';

/** The deploy environment the checkout was started from; the backend reads `custom_data.env`. */
const ENV_PARAM = 'checkout[custom][env]';

/**
 * Build the checkout URL for a signed-in user, or null when it can't be built
 * safely — unset/malformed env var, non-http scheme, or no user id.
 *
 * Returning null rather than throwing lets the caller disable the button on a
 * misconfigured deploy instead of crashing the settings page.
 */
export function buildCheckoutUrl(
	baseUrl: string | undefined,
	userId: string,
	deployTier: DeployTier = resolveDeployTier(),
): string | null {
	if (!baseUrl || !userId) return null;

	let url: URL;
	try {
		url = new URL(baseUrl);
	} catch {
		return null;
	}

	// The base comes from an env var. Anything but http(s) reaching a navigation
	// would turn a misconfigured deploy into a script-execution sink.
	if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

	// `set`, not `append`: a base that already carries a user id or env gets
	// replaced, never duplicated.
	url.searchParams.set(USER_ID_PARAM, userId);
	url.searchParams.set(ENV_PARAM, deployTier);
	return url.toString();
}
